import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { passwordIcon, phone, userMail } from '../assets/icons';
import InputRHF from '../components/inputs/InputRHF';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { signUpSchema } from '../schema/auth';
import { requestOtp, register } from '../services/auth';
import MediatorPopup from './MediatorPopup';
import RegistrationSuccess from './shared/RegistrationSuccess';
import { useNavigate } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import { toast } from 'react-toastify';
import { useContext } from 'react';
import { UserContext } from '../context/UserContext';
import { checkPhoneExist, getUserData } from '../services/apis/user';
import { toastConfig } from '../helpers/helper';
import MConfirmation from './modals/MConfirmation';
import VerifyRegistration from './auth/VerifyRegistration';

const SignUpForm = () => {
  const { t } = useTranslation();
  const [selectedClientType, setSelectedClientType] = useState(2);
  const [isModal, setIsModal] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [openOTPModal, setOpenOTPModal] = useState(false);
  const [data, setData] = useState({});

  const [isRegisterSuccess, setIsRegisterSuccess] = useState(false);
  const navigate = useNavigate();
  const { setUserData } = useContext(UserContext);

  const CloseModal = () => {
    setIsModal(false);
  };

  const {
    register: formRegister,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm({ resolver: zodResolver(signUpSchema) });

  const handleSignUp = async () => {
    const { phoneNumber, password ,confirmPass} = getValues();

    const result = signUpSchema.safeParse({ phoneNumber, password ,confirmPass});

    if (!selectedClientType) {
      alert('Please select a client type.');
      return;
    }

    if (!result.success) {
      toast.error(result.error.errors[0].message, { position: 'top-right' });
      return;
    }


    const { phoneNumber: transformedPhoneNumber, password: validPassword } =
      result.data;

      const responsePhoneExist = await checkPhoneExist(transformedPhoneNumber)

      if(responsePhoneExist?.data?.phoneExists == true){
        toast.error(t("login.schema.numberAlreadyExist"),toastConfig);
      }else{
      

    
      const otpResponse = await requestOtp(transformedPhoneNumber);
      const otpCode = otpResponse;

      const roleId = selectedClientType;
      const dataForm = {
        phoneNumber: transformedPhoneNumber,
        password: validPassword,
        otpCode,
        roleId,
        imgUrl: '',
      };

      setData(dataForm)
      setOpenOTPModal(true)
    }
  };


  return (
    <div className="rounded-lg max600:order-2 w-full max-w-md space-y">
      <div className="text-2xl font-bold text-center text-customPurple sm:mt-10">
        {t('login.label.welcome')}
      </div>
      <div className="text-l text-center mt-3">
        {t('login.label.createNewAccount')}
      </div>
      <form onSubmit={handleSubmit(handleSignUp)} className="mt-9">
        <div className="mt-9">
          <InputRHF
            label={t('contactUs.label.phone')}
            type="text"
            icon={userMail}
            placeholder={t('contactUs.placeholder.phone')}
            {...formRegister('phoneNumber')}
            error={errors.phoneNumber?.message}
          />
        </div>
        <div className='mt-5'>
      <div className="flex flex-row items-center text-[#3E0292] text-[12px]">
        <img src={userMail} />
        <div className="mx-3 font-400">{t("contactUs.label.typeUser")}</div>
      </div>
        <div className="flex space-x-4 justify-center mt-3 rtl:space-x-reverse">
          <button
            type="button"
            className={`px-16 py-4 rounded-lg ${
              selectedClientType === 2
                ? 'bg-[#D5FFFB] border-[3px] border-[#00CEBC] text-[#303030]'
                : 'bg-[#EFEFEF] text-[#9E9E9E]'
            }`}
            onClick={() => setSelectedClientType(2)}
          >
            {t('login.label.buyer')}
          </button>

          <button
            type="button"
            className={`px-16 py-4 rounded-lg ${
              selectedClientType === 3
                ? 'bg-[#D5FFFB] border-[3px] border-[#00CEBC] text-[#303030]'
                : 'bg-[#EFEFEF] text-[#9E9E9E]'
            }`}
            onClick={() => setSelectedClientType(3)}
          >
            {t('login.label.broker')}
          </button>
        </div>
        </div>
        <div className="mt-5">
          <InputRHF
            label={t('login.label.password')}
            type="password"
            icon={passwordIcon}
            placeholder={t('login.placeholder.password')}
            {...formRegister('password')}
            error={errors.password?.message}
          />
        </div>
        <div className="mt-5">
          <InputRHF
            label={t('login.label.confirmPass')}
            type="password"
            icon={passwordIcon}
            placeholder={t('login.placeholder.confirmPass')}
            {...formRegister('confirmPass')}
            error={errors.confirmPass?.message}
          />
        </div>
        <p className="text-start text-gray-500 mt-5">
          <input type="checkbox" className="form-checkbox mr-2 ml-2" onChange={(e)=>setAcceptTerms(e.target.checked)} checked={acceptTerms} />
          
          <a
            href="/terms"
            className="text-sm text-[#3E0292] font-normal hover:underline ml-1"
          >
            {t('login.label.terms')}
          </a>
        </p>

        <div className="flex justify-center mt-5">
          <button
            disabled={acceptTerms ? false : true}
            type="submit"
            className="bg-[#00CEBC] border border-[#00CEBC] rounded-[14px] text-[15px] text-white px-10 py-4 w-[70%] shadow-lg text-center w-64"
          >
            {t('login.label.createNewAccount')}
          </button>
        </div>
      </form>

      <p className="text-center text-gray-500 mt-5">
        {t('login.label.NoAccount')}
        <a
          href="/login"
          className="text-[#3E0292] font-normal hover:underline ml-1"
        >
          {t('login.label.login')}
        </a>
      </p>
      <MediatorPopup isOpen={isModal} isClose={CloseModal} />
      <RegistrationSuccess isOpen={isRegisterSuccess} />
      <MConfirmation openModal={openOTPModal} content={<VerifyRegistration handleSignUp={handleSignUp} body={data} closeOtpModal={()=>setOpenOTPModal(false)}/>} setOpenModal={()=>setOpenOTPModal(false)} />

    </div>
  );
};

export default SignUpForm;
